import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { getAllDocumentCategorys } from 'src/shared/services/documentCategoryService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Box } from '@mui/system';
import { DocumentCategorySearchFilter } from '../models/documentCategorySearchFilter';
import { DocumentCategory } from '../models/documentCategory';

type DocumentCategoryDataGridProps = {
  refetchDataGrid: boolean;
  setCategoryId: (categoryId: number) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
  setOpenEditCategory: (openEditArticle: boolean) => void;
  searchFilter: DocumentCategorySearchFilter;
  searchFilterCallback: (searchFilter: DocumentCategorySearchFilter) => void;
};

export const DocumentCategoryDataGrid = (props: DocumentCategoryDataGridProps) => {
  const { refetchDataGrid, setCategoryId, setRefetchDataGrid, setOpenEditCategory, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<DocumentCategory[]>([]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllDocumentCategorys(searchFilter);
      const categoryResult = extendObjectWithId(result.data.results, 'documentCategory_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(categoryResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setRefetchDataGrid(false);
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter, setRefetchDataGrid]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (refetchDataGrid) {
      fetchData();
    }
  }, [fetchData, refetchDataGrid]);

  const onRowClick = (params) => {
    if (params.row && params.row.documentCategory_id) {
      setCategoryId(params.row.documentCategory_id);
      setOpenEditCategory(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'category',
      headerName: t('documentArchive:documentCategory.name'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.category || ''}</>;
      },
    },
    {
      field: 'description',
      headerName: t('documentArchive:documentCategory.description'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.description || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<DocumentCategory>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        sortModel={[
          {
            field: searchFilter.sortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
